import { useState, useEffect } from 'react';

const projectImages = [
  {
    src: '/images/panel.jpg',
    alt: 'Bedienpanel – HMI-Steuerung',
    category: 'Steuerungstechnik',
  },
  {
    src: '/images/schrank1.jpg',
    alt: 'Beckhoff SPS-Schaltschrank',
    category: 'Steuerungstechnik',
  },
  {
    src: '/images/ref1.jpg',
    alt: 'Maschinenanlage – Sondermaschinenbau',
    category: 'Sondermaschinen',
  },
  {
    src: '/images/anlage2.jpg',
    alt: 'Automatisierungsanlage',
    category: 'Automation',
  },
  {
    src: '/images/detail1.jpg',
    alt: 'Detailaufnahme Mechanik',
    category: 'Mechanische Fertigung',
  },
  {
    src: '/images/sps1.jpg',
    alt: 'SPS-Programmierung',
    category: 'Steuerungstechnik',
  },
  {
    src: '/images/sps2.jpg',
    alt: 'SPS-Schaltschrank Innenansicht',
    category: 'Steuerungstechnik',
  },
  {
    src: '/images/ref2.jpg',
    alt: 'Referenzprojekt Handling',
    category: 'Automation',
  },
  {
    src: '/images/ref3.jpg',
    alt: 'Referenzprojekt Anlage',
    category: 'Sondermaschinen',
  },
];

const categories = ['Alle', 'Sondermaschinen', 'Steuerungstechnik', 'Automation', 'Mechanische Fertigung'];

const ProjectGallery = () => {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    if (lightbox) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const filtered = activeCategory === 'Alle'
    ? projectImages
    : projectImages.filter((img) => img.category === activeCategory);

  return (
    <section className="py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-0.5 bg-[#DC2626]"></span>
            <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Einblicke</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Projektbilder</h2>
          <p className="text-gray-500 text-base max-w-xl">
            Echte Aufnahmen aus unseren realisierten Projekten – von der Steuerungstechnik bis zur fertigen Anlage.
          </p>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#1E3A8A] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((img) => (
            <div
              key={img.src}
              className="group relative w-full h-[240px] rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
              onClick={() => setLightbox({ src: img.src, alt: img.alt })}
              role="button"
              tabIndex={0}
              aria-label={`Bild vergrößern: ${img.alt}`}
              onKeyDown={(e) => e.key === 'Enter' && setLightbox({ src: img.src, alt: img.alt })}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                <div className="p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-xs font-semibold text-[#DC2626] uppercase tracking-wider">{img.category}</span>
                  <p className="text-white text-sm font-medium mt-1">{img.alt}</p>
                </div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                <i className="ri-zoom-in-line text-[#1E3A8A] text-sm"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            onClick={() => setLightbox(null)}
            aria-label="Lightbox schließen"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-[85vh] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">{lightbox.alt}</p>
        </div>
      )}
    </section>
  );
};

export default ProjectGallery;
